import { Component, EventEmitter, Input, Output, ElementRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { FormControl, FormGroup } from '@angular/forms';
import { Observable, finalize, of } from 'rxjs';
import { NotesService } from '@feature/user-details/account-notes/notes.service';

@Component({
    template: '',
    standalone: false
})
export class NoteTemplate<TTemplate> {
  protected _isEditable!: boolean;
  protected noteDetailData: any;
  protected noteSidebarData!: BehaviorSubject<any>;
  protected noteInfoSubject = new BehaviorSubject<any>(null);
  noteInfo: Observable<any> = this.noteInfoSubject.asObservable();

  constructor(
    protected readonly notesService: NotesService,
    protected elRef?: ElementRef
  ) { }

  @Input() isExistingNote!: boolean;
  @Input() set isEditable(value: boolean) {
    this._isEditable = value;
  };
  get isEditable(): boolean {
    return this._isEditable;
  }
  @Input() set noteDetail(value: TTemplate) {
    this.noteDetailData = value
      ? { ...value }
      : null;
  }
  get noteDetail(): TTemplate {
    return this.noteDetailData;
  }
  @Input() isSidebarViewMode!: boolean;
  @Input() viewSource: any;

  @Output() initializationCompleted = new EventEmitter<void>();
  @Output() noteCreated = new EventEmitter<string>();

  public isLoading = false;
  public noteForm!: FormGroup;
  public saveDraftEnable = false;
  isAutoPopulate: boolean = false;

  /**
   * @summary
   *  Should be called by the ngOnInit of the implementing component.
   *  Creates and populates the form with either new note or existing
   *  note detail.
   */
  init(): void {
    this.noteForm = this.createForm();

    if (!this.isExistingNote) {
      this.getStaticFields();
    } else {
      this.noteForm.addControl('noteId', new FormControl());
      this.fillExistingNote();
    }

    if (!this.isEditable) {
      this.noteForm.disable();
    }
  }

  /**
   * @summary
   *  Creates the form group which the user will interact
   *  with to populate notes.
   */
  public createForm(): FormGroup {
    return new FormGroup({});
  };

  private fillExistingNote(): void {
    this.mutateNoteDetail(this.noteDetail);

    this.loadData(this.noteDetail).subscribe(() => {
      this.isAutoPopulate = false;
      this.noteInfoSubject.next(this.noteDetail);
      this.patchFormWithExistingNote();
      this.initializationCompleted.emit();
    });
  }

  /**
   * @summary
   *  An entrypoint for a note template to load data required by the template
   *  after mutating the data and prior to patching the form.
   * @param data
   *  The data containing the values with which to populate the NoteForm.
   */
  loadData(existingNote: TTemplate): Observable<any> {
    return of({});
  }

  /**
   * @summary
   *  Mutates the note detail before populating the form.
   * @param data
   *  The data containing the values with which to populate the NoteForm.
   */
  protected mutateNoteDetail(data: TTemplate) {
    // keep to allow overriding.
  }

  /**
   * @summary
   *  Mutates the static note data before it is assigned.
   * @param data
   *  The data that should be mutated.
   */
  protected mutateStaticData(data: any) {
    // can be overridden, keep this method.
  }

  /**
     * @summary
     *  Patches the note form with the existing note. Can be overridden to
     *  implement note-specific logic.
     */
  protected patchFormWithExistingNote() {
    // can be overridden, keep this method.
    this.noteForm.patchValue(this.noteDetail as any);
  }

  /**
   * @summary
   *  Patches the note form with the static data. Can be overridden to
   *  implement note-specific logic.
   */
  protected patchFormWithStaticData(data: any) {
    // can be overridden, keep this method.
    this.noteForm.patchValue(data);
  }

  /**
   * @summary
   *  Gets note data and populates the form with the note data.
   */
  private getStaticFields() {}

  /**
     * @summary
     *  Called when a note is successfully saved.
     * @param type
     *  The broadcast type for a successful message.
     */
  protected onSaved(note: TTemplate, result: TTemplate, type: string) {
    // can be overridden, keep this method.

    if (type === 'success') {
      this.noteCreated.emit((result as any).noteId);
    }
  }

  /**
   * @summary
   *  Saves the note.
   * @param note
   *  The note to save.
   */
  public save(note: any): void {
    if (!this.noteForm.valid)
      return;

    const req = this.isExistingNote
      ? this.notesService.updateNoteByNoteId(note)
      : this.notesService.createNewNote(note);

    var successfulBroadcastType = this.isExistingNote
      ? "update"
      : "success";

    this.isLoading = true;
    req
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (result: any) => this.onSaved(note, result, successfulBroadcastType),
      });
  }

  /**
   * @summary
   *  Saves the note as a Draft.
   * @param note
   *  The note to save.
   */
  public saveDraft(note: any): void {
    const req = this.isExistingNote
      ? this.notesService.updateNoteByNoteId(note)
      : this.notesService.createNewNote(note);

    var successfulBroadcastType = this.isExistingNote
      ? "update"
      : "success";

    this.isLoading = true;
    req
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (result: any) => this.onSaved(note, result, successfulBroadcastType),
      });
  }
}
